/* eslint-disable complexity */
import { getUseCases } from 'app/api/Usecase';
import { t } from 'i18next';

import {
  buildConfusionMatrixPayload,
  CUMULATIVE_ROW_LABEL,
  getTotalMisclassificationCount,
  isMisclassificationPair,
  sortDefectsForMatrix as sortDefectsForMatrixPure,
} from './confusionMatrix.transforms';
import {
  DEFECT_DISTRIBUTION_CONSTANTS,
  DELETE_MISSCLASSIFICATION_IMAGES_ROW_IDS,
  RESET_DEFECT_DISTRIBUTION_DATA,
  SET_ACTIVE_USECASE_COUNT,
  SET_CONFUSION_MODEL,
  SET_CONFUSION_USE_CASE,
  SET_DEFECT_DISTRIBUTION_DATA,
  SET_DEFECT_DISTRIBUTION_INDIVIDUAL_LOADING,
  SET_DRAWER_STATE,
  SET_DRAWER_STATUS,
  SET_DRAWER_USECASE,
  SET_MISSCLASSIFICATION_IMAGES_ROW_IDS,
  SET_MODE,
  SET_UNIT,
} from './constants';

/**
 * Re-export of the pure matrix sort, preserving the original 3-arg signature
 * and i18next-translated cumulative-row label. The heavy implementation now
 * lives in `confusionMatrix.transforms` so it can also run inside a worker.
 */
export const sortDefectsForMatrix = (order, defects, isCollapsed) =>
  sortDefectsForMatrixPure(
    order,
    defects,
    isCollapsed,
    t(CUMULATIVE_ROW_LABEL)
  );

export function setDrawerState(payload) {
  return { type: SET_DRAWER_STATE, payload };
}

export function setDrawerStatus(payload) {
  return { type: SET_DRAWER_STATUS, payload };
}

export function setMode(payload) {
  return { type: SET_MODE, payload };
}

export function setUnit(payload) {
  return { type: SET_UNIT, payload };
}

export function setConfusionUsecase(payload) {
  return { type: SET_CONFUSION_USE_CASE, payload };
}

export function setConfusionModel(payload) {
  return { type: SET_CONFUSION_MODEL, payload };
}

export function setDrawerUsecase(payload) {
  return { type: SET_DRAWER_USECASE, payload };
}

export function setActiveUsecaseCount(subscriptionId) {
  return dispatch => {
    getUseCases({
      baseParams: {
        limit: 1,
        model_status__in: 'deployed_in_prod',
        subscription_id: subscriptionId,
        useV2: true,
      },
    }).then(_ => {
      const payload = _.count || null;
      dispatch({ type: SET_ACTIVE_USECASE_COUNT, payload });
    });
  };
}

export function setMisclassificationImagesRowIds(payload) {
  return { type: SET_MISSCLASSIFICATION_IMAGES_ROW_IDS, payload };
}

export function deleteMisclassificationImagesRowIds(payload) {
  return { type: DELETE_MISSCLASSIFICATION_IMAGES_ROW_IDS, payload };
}

export function setDefectDistributionIndividualLoading(payload) {
  return { type: SET_DEFECT_DISTRIBUTION_INDIVIDUAL_LOADING, payload };
}

export function resetDefectDistributionData() {
  return { type: RESET_DEFECT_DISTRIBUTION_DATA };
}

const get2dMatrix = confusionMatrix => {
  const default_row = {
    count: 0,
    automated_count: 0,
    audited_fileset_count: 0,
  };
  const defects = getDefectList(confusionMatrix);

  const defectsMap = defects.reduce(
    (prev, curr, index) => ({
      ...prev,
      [curr]: index,
    }),
    {}
  );
  const result = defects.map(() => Array(defects.length).fill(default_row));
  confusionMatrix.forEach(row => {
    result[defectsMap[row['ai_defect']]][defectsMap[row['gt_defect']]] = row;
  });
  return result;
};

const getDefectList = confusionMatrix => {
  const defects = [];
  confusionMatrix.forEach(row => {
    defects.push(row['gt_defect']);
    defects.push(row['ai_defect']);
  });
  return [...new Set(defects)];
};

export function setDefectBasedDistribution(payload) {
  const { confusion_matrix, defects } = payload;
  const confusion_matrix_2d = get2dMatrix(confusion_matrix);
  const defectsList = getDefectList(confusion_matrix);

  const result = confusion_matrix_2d.map((_, index) => {
    let tp = 0;
    let fp = 0;
    let fn = 0;
    let total_fileset_count = 0;
    let automated_fileset_count = 0;
    let audited_fileset_count = 0;
    let total_gt_defects = 0;
    let total_model_defects = 0;
    let manual_fileset_count = 0;

    confusion_matrix_2d.map((_, ai_index) => {
      if (ai_index === index) {
        tp += confusion_matrix_2d[index][ai_index]['count'];
      } else if (
        confusion_matrix_2d[index][ai_index]?.ai_defect !== -1 &&
        confusion_matrix_2d[index][ai_index]?.gt_defect !== -1
      ) {
        fp += confusion_matrix_2d[index][ai_index]['count'];
      }

      if (confusion_matrix_2d[index][ai_index]?.gt_defect !== -1) {
        total_gt_defects += confusion_matrix_2d[index][ai_index]['count'];
      }
    });

    confusion_matrix_2d.map((_, ai_index) => {
      if (ai_index !== index) {
        fn += confusion_matrix_2d[ai_index][index]['count'];
      }
      if (confusion_matrix_2d[ai_index][index]?.ai_defect !== -1) {
        total_model_defects += confusion_matrix_2d[ai_index][index]['count'];
      }
      total_fileset_count += confusion_matrix_2d[ai_index][index]['count'];
      if (confusion_matrix_2d[ai_index][index]?.ai_defect !== -1) {
        automated_fileset_count +=
          confusion_matrix_2d[ai_index][index]['count'];
      } else if (confusion_matrix_2d[ai_index][index]?.ai_defect === -1) {
        manual_fileset_count += confusion_matrix_2d[ai_index][index]['count'];
      }
      audited_fileset_count +=
        confusion_matrix_2d[ai_index][index]['audited_fileset_count'];
    });

    return {
      name: defects[defectsList[index]]?.name || '',
      defect_id: defectsList[index],
      total: total_fileset_count,
      auto: automated_fileset_count,
      manual: manual_fileset_count,
      audited: audited_fileset_count,
      correct: tp,
      missed: fn,
      extra: fp,
      recall_percentage: total_model_defects
        ? Math.round((tp * 100) / total_model_defects)
        : null,
      precision_percentage: total_gt_defects
        ? Math.round((tp * 100) / total_gt_defects)
        : null,
    };
  });

  return {
    type: SET_DEFECT_DISTRIBUTION_DATA,
    payload: {
      type: DEFECT_DISTRIBUTION_CONSTANTS.DEFECT_BASED_DISTRIBUTION,
      data: result.filter(item => item.defect_id !== -1),
    },
  };
}

/**
 * Wraps an already-computed confusion-matrix payload ({ matrix, matrixMeta })
 * into the redux action, skipping recomputation. Dispatched with the result of
 * the off-thread worker build (see `useConfusionMatrixWorker`).
 */
export function setConfusionMatrixResult(data) {
  return {
    type: SET_DEFECT_DISTRIBUTION_DATA,
    payload: {
      type: DEFECT_DISTRIBUTION_CONSTANTS.CONFUSION_MATRICS,
      data,
    },
  };
}

/**
 * Synchronous build (kept for backwards compatibility / non-worker callers).
 * The heavy O(D²·M) work lives in `confusionMatrix.transforms`.
 */
export function setConfusionMatrics(payload) {
  return setConfusionMatrixResult(buildConfusionMatrixPayload(payload));
}

export function setMisclassificationPairs(payload) {
  const { confusion_matrix, defects } = payload;

  const totalMisclassificationCount =
    getTotalMisclassificationCount(confusion_matrix);

  const result = confusion_matrix
    .reduce((prev, curr) => {
      if (isMisclassificationPair(curr.gt_defect, curr.ai_defect)) {
        return [
          ...prev,
          {
            ai_defect_id: curr['ai_defect'],
            gt_defect_id: curr['gt_defect'],
            ai_defect: defects[curr['ai_defect']] || {},
            gt_defect: defects[curr['gt_defect']] || {},
            misclassification_count: curr['count'],
            misclassification_percent:
              (100 * curr['count']) / totalMisclassificationCount,
          },
        ];
      }

      return prev;
    }, [])
    .sort((a, b) => b.misclassification_count - a.misclassification_count);

  return {
    type: SET_DEFECT_DISTRIBUTION_DATA,
    payload: {
      type: DEFECT_DISTRIBUTION_CONSTANTS.MISCLASSIFICATION_PAIR,
      data: result,
    },
  };
}
